import type { LocationGroup, SiteSearchData } from './types';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function headers(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '');
}

const RETRY_DELAYS_MS = [1500, 3000];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/**
 * GET with two retries on a 5xx or network error: this runs on a public page,
 * and a brief edge hiccup should not show visitors an error card.
 */
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    try {
      const res = await fetch(url, { headers: headers(), signal });
      if (res.ok) return (await res.json()) as T;
      lastError = new Error(`${url} responded ${res.status}`);
      if (res.status < 500) break; // a 4xx will not fix itself
    } catch (err) {
      if (signal?.aborted) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

/** The events, groups and serve opportunities matching the visitor's words. */
export function fetchSearch(
  apiBaseUrl: string,
  query: string,
  signal?: AbortSignal
): Promise<SiteSearchData> {
  return getJson<SiteSearchData>(
    `${base(apiBaseUrl)}/api/search?q=${encodeURIComponent(query)}`,
    signal
  );
}

/** Campuses grouped by location, with each location's brand SVG (for the campus chips). */
export async function fetchLocations(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<LocationGroup[]> {
  const data = await getJson<unknown>(`${base(apiBaseUrl)}/api/locations`, signal);
  // An error body ({ error }) must not take the widget down with it.
  return Array.isArray(data) ? (data as LocationGroup[]) : [];
}
